import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { RecertFormData } from 'components/Assessments/RecertAssessment/types/RecertFormData'
import { RecertFormField } from 'components/Assessments/RecertAssessment/types/RecertFormField'
import { RecertAssessmentQuestionnaireItem } from 'components/Assessments/RecertAssessment/types/questionnaire'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'

/**
 * Therapies subsection adapter
 * Handles therapy services and home exercise plans (5 fields)
 */
export class TherapiesAdapter extends BaseSubsectionAdapter<RecertFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RecertFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Direct field extractions
    const therapies = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_THERAPIES
    )

    const therapyProvider = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_THERAPY_PROVIDER
    )?.at(0)

    const homeExercisePlan = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_HOME_EXERCISE_PLAN
    )?.at(0)

    const homeExerciseDuration = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_HOME_EXERCISE_DURATION
    )?.at(0)

    const homeExerciseFrequency = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_HOME_EXERCISE_FREQUENCY
    )?.at(0)

    return {
      [RecertFormField.THERAPIES_THERAPIES]: therapies,
      [RecertFormField.THERAPIES_THERAPY_PROVIDER]: therapyProvider,
      [RecertFormField.THERAPIES_HOME_EXERCISE_PLAN]: homeExercisePlan,
      [RecertFormField.THERAPIES_HOME_EXERCISE_DURATION]: homeExerciseDuration,
      [RecertFormField.THERAPIES_HOME_EXERCISE_FREQUENCY]: homeExerciseFrequency
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(RecertFormField.THERAPIES_THERAPIES)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_THERAPIES,
        value: this.getFormDataField(RecertFormField.THERAPIES_THERAPIES)
      })
    }

    if (this.formDataFieldExists(RecertFormField.THERAPIES_THERAPY_PROVIDER)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_THERAPY_PROVIDER,
        value: this.getFormDataField(RecertFormField.THERAPIES_THERAPY_PROVIDER)
      })
    }

    if (this.formDataFieldExists(RecertFormField.THERAPIES_HOME_EXERCISE_PLAN)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_HOME_EXERCISE_PLAN,
        value: this.getFormDataField(RecertFormField.THERAPIES_HOME_EXERCISE_PLAN)
      })
    }

    if (this.formDataFieldExists(RecertFormField.THERAPIES_HOME_EXERCISE_DURATION)) {
      mutations.push({
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_HOME_EXERCISE_DURATION,
        value: this.getFormDataField(RecertFormField.THERAPIES_HOME_EXERCISE_DURATION)
      })
    }

    if (this.formDataFieldExists(RecertFormField.THERAPIES_HOME_EXERCISE_FREQUENCY)) {
      mutations.push({
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_HOME_EXERCISE_FREQUENCY,
        value: this.getFormDataField(RecertFormField.THERAPIES_HOME_EXERCISE_FREQUENCY)
      })
    }

    return mutations
  }
}
